import { filter } from 'ramda';
import { useMemo } from 'react';
import type { MKEERAApiResponseItem } from '../dashboard.types';
import { formatDistrict, parseAmount } from '../utils/dataProcessing';

interface UseAssistanceTypeChartDataParams {
  data: MKEERAApiResponseItem[];
  selectedDistrict?: string;
}

export function useAssistanceTypeChartData({ data, selectedDistrict }: UseAssistanceTypeChartDataParams) {
  return useMemo(() => {
    // Safety check: ensure data is an array
    if (!Array.isArray(data) || data.length === 0) {
      return {
        labels: [],
        data: [],
        entries: [],
      };
    }

    // Filter data by selected district
    const filteredData = selectedDistrict
      ? filter((item: MKEERAApiResponseItem) => formatDistrict(item.applicationData.aldermanicDistrict) === selectedDistrict, data)
      : data;

    const sums: Record<string, number> = {};
    const counts: Record<string, number> = {};
    filteredData.forEach((item) => {
      const key = item.applicationData.typeOfAssistance?.trim() || 'Unknown';
      sums[key] = (sums[key] || 0) + parseAmount(item.applicationData.amountOfPayment);
      counts[key] = (counts[key] || 0) + 1;
    });

    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    const labels = Object.keys(sums);
    const entries = labels
      .map((label) => ({
        label,
        value: sums[label],
        formatted: sums[label].toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        }),
        count: counts[label],
        percent: total ? Math.round((counts[label] / total) * 1000) / 10 : 0,
      }))
      .sort((a, b) => b.value - a.value);
    return {
      labels: entries.map((e) => e.label),
      data: entries.map((e) => e.value),
      entries,
    };
  }, [data, selectedDistrict]);
}
